import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  useDeleteCompanyLogo,
  useGetCompanyById,
  useGetCompanyLogoSignedUrl,
  useUpdateCompanyData,
  useUploadCompanyImage
} from "../../../../api/company";
import { successMessage } from "../../../../helpers/ErrorHandler";
import { useCompanyStaticOptions } from "../../shared/hooks/useCompanyStaticOptions";
import { CompanyFormData } from "../../shared/types/companyForm.types";
import { CompanyEditModalProps, CompanyEditViewModel } from "../types/companyEditView.types";

const toInputDate = (value?: string) => {
  const date = value ? new Date(value) : null;
  return date && !isNaN(date.getTime()) ? date.toISOString().slice(0, 10) : "";
};

/**
 * Ports the exact state/submit logic of the legacy `CompanyEditModal`
 * (src/screens/company/companyEdit/CompanyEditModal.tsx): the company is
 * loaded through the same `useGetCompanyById` mutation each time the drawer
 * opens, the PUT payload is the same shape (`isParent:
 * false`, `tenantType: Tenant`, trimmed name, chosen country/currency/date),
 * a newly picked logo is uploaded *after* the PUT succeeds (with the same
 * "logo upload failed" fallback message), and removing the logo deletes the
 * persisted one immediately and refreshes the details.
 *
 * Differences from legacy: `status` is now an editable field (legacy forced
 * ACTIVE on every save), `industry` is now pre-filled from the loaded
 * company (the legacy form never populated it, so saving blanked it), and an
 * unchanged subscription date is sent back as the original stored value.
 */
export const useCompanyEditViewModel = ({
  visibleModal,
  id,
  closeModal,
  onSuccess
}: CompanyEditModalProps): CompanyEditViewModel => {
  const options = useCompanyStaticOptions();
  const [imageData, setImageData] = useState<File | null>(null);

  const {
    data: companyDetails,
    isLoading: isLoadingDetails,
    mutate: loadCompany,
    reset: resetCompany
  } = useGetCompanyById();
  const {
    isLoading: isUpdating,
    mutate: updateCompany,
    reset: resetUpdate
  } = useUpdateCompanyData();
  const { isLoading: isUploadingLogo, mutate: uploadCompanyImage } = useUploadCompanyImage();
  const { isLoading: isDeletingLogo, mutate: deleteCompanyLogo } = useDeleteCompanyLogo();
  const {
    data: logoSignedUrlData,
    status: logoStatus,
    fetchStatus: logoFetchStatus
  } = useGetCompanyLogoSignedUrl(companyDetails?.logoName);

  const values = useMemo<CompanyFormData>(
    () => ({
      name: companyDetails?.name || "",
      address: companyDetails?.address || "",
      companyEmail: companyDetails?.companyEmail || "",
      phone: companyDetails?.phone || "",
      registrationNumber: companyDetails?.registrationNumber || "",
      website: companyDetails?.website || "",
      industry: companyDetails?.industry || "",
      noOfEmployees: companyDetails?.noOfEmployees || "",
      noOfUsers: companyDetails?.noOfUsers || 1,
      noOfProducts: companyDetails?.noOfProducts || "",
      noOfSKUS: companyDetails?.noOfSKUS || 1,
      tenantCountry:
        companyDetails?.tenantCountry !== undefined && companyDetails?.tenantCountry !== null
          ? String(companyDetails.tenantCountry)
          : "",
      zipCode: companyDetails?.zipCode || "",
      currency: companyDetails?.currency?._id ? String(companyDetails.currency._id) : "",
      dateOfSubscription: toInputDate(companyDetails?.dateOfSubscription),
      status: companyDetails?.status || "ACTIVE"
    }),
    [companyDetails]
  );

  const methods = useForm<CompanyFormData>({ mode: "all", defaultValues: values, values });
  const { handleSubmit, reset } = methods;

  useEffect(() => {
    if (visibleModal && id) {
      loadCompany(id);
    }
    return () => {
      setImageData(null);
      reset();
      resetCompany();
      resetUpdate();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleModal]);

  const finish = (message: string) => {
    successMessage(message);
    closeModal();
    onSuccess();
  };

  const onSubmit = (data: CompanyFormData) => {
    const unchangedDate =
      data.dateOfSubscription === toInputDate(companyDetails?.dateOfSubscription);
    const payload = {
      name: data.name.trim(),
      address: data.address,
      companyEmail: data.companyEmail,
      phone: data.phone,
      registrationNumber: data.registrationNumber,
      website: data.website,
      industry: data.industry,
      noOfEmployees: data.noOfEmployees,
      noOfUsers: data.noOfUsers,
      noOfProducts: data.noOfProducts,
      noOfSKUS: data.noOfSKUS,
      zipCode: data.zipCode,
      status: data.status || "ACTIVE",
      isParent: false,
      tenantCountry: options.resolveCountry(data.tenantCountry) ?? companyDetails?.tenantCountry,
      tenantType: "Tenant",
      dateOfSubscription: unchangedDate
        ? companyDetails?.dateOfSubscription || ""
        : data.dateOfSubscription
        ? new Date(data.dateOfSubscription)
        : "",
      currency: options.resolveCurrency(data.currency) ?? companyDetails?.currency?._id
    };

    updateCompany(
      { id: companyDetails?._id, data: payload },
      {
        onSuccess: () => {
          if (imageData && companyDetails?._id) {
            uploadCompanyImage(
              {
                company_id: companyDetails._id,
                file: imageData,
                customFileName: companyDetails._id
              },
              {
                onSuccess: () => finish("Company data and logo updated successfully"),
                onError: () => finish("Company data updated successfully (logo upload failed)")
              }
            );
          } else {
            finish("Company data updated successfully");
          }
        }
      }
    );
  };

  const onLogoRemove = () => {
    setImageData(null);
    if (companyDetails?._id && companyDetails?.logoName) {
      deleteCompanyLogo(companyDetails._id, {
        onSuccess: () => {
          successMessage("Company logo deleted successfully");
          loadCompany(id);
        },
        onError: () => {
          successMessage("Failed to delete logo");
        }
      });
    }
  };

  const onCancel = () => {
    closeModal();
    reset();
  };

  return {
    methods,
    countries: options.countries,
    currencies: options.currencies,
    isCountriesLoading: options.isCountriesLoading,
    isCurrencyLoading: options.isCurrencyLoading,
    isLoadingDetails,
    isLoadingLogo: logoStatus === "loading" && logoFetchStatus === "fetching",
    logoUrl: logoSignedUrlData?.data ?? null,
    onLogoSelect: setImageData,
    onLogoRemove,
    isSaving: isUpdating || isUploadingLogo || isDeletingLogo,
    onSave: handleSubmit(onSubmit),
    onCancel
  };
};
